/**
 * Per-location service-type availability (ADR-0051).
 *
 * `locations.disabledServiceTypes` is a blacklist on the *how* axis (Hol- und
 * Bring-Service vs. Selbstvorfahren) — not to be confused with
 * `disabledServiceIds`, which blacklists Prüfarten (the *what* axis). It rides
 * along on the `getLocationOpeningHours` response for the hub FleetHub resolves
 * from the company's `expansionRegion`.
 *
 * The field is additive and the backend deploys first, so an absent key must be
 * read as "both service types are offered" — that is what an old backend, an
 * unconfigured location, and the fallback opening hours all produce.
 */

/** The two ways an inspection can reach the Prüfstelle. */
export type FleetServiceType = "pickup_delivery" | "driveby"

/** Shape this module reads off the callable response. */
export interface ServiceTypeAvailabilitySource {
  disabledServiceTypes?: FleetServiceType[]
}

/**
 * True when the hub does not offer Hol- und Bring-Service.
 *
 * Deliberately tolerant: the argument is wire data that may be `null` while the
 * opening hours are still loading, may predate the backend that emits the field,
 * or may carry a non-array value if a future writer gets it wrong. Anything that
 * is not an array explicitly containing `"pickup_delivery"` means "offered",
 * because refusing HBS on malformed input would silently downgrade every booking
 * — and the server gate is the real enforcement point anyway.
 */
export function isHbsDisabledAtHub(
  openingHours: ServiceTypeAvailabilitySource | null | undefined,
): boolean {
  const disabled: unknown = openingHours?.disabledServiceTypes
  if (!Array.isArray(disabled)) return false
  return disabled.includes("pickup_delivery")
}
